import { Heart, Trash2, ShoppingCart, ArrowRight, Loader } from "lucide-react";
import { Link } from "react-router-dom";
import { getLanguage } from "../../../utils/Helper";
import "./Wishlist.scss";
import { useWishlist } from "../../../context/WishlistContext";
import { useCart } from "../../../context/CartContext";
import { useAuth } from "../../../context/AuthContext";
import { useState } from "react";

const Wishlist = () => {
  const [currentLanguage] = useState(getLanguage());
  const authContext = useAuth();

  // Use Wishlist Context
  const {
    wishlistItems,
    wishlistCount,
    isLoading,
    removeFromWishlist,
    clearWishlist,
    moveToCart,
  } = useWishlist();

  const [processingItems, setProcessingItems] = useState(new Set());

  const handleRemoveItem = async (item) => {
    // Prevent multiple clicks
    if (processingItems.has(item.id)) return;

    console.log("Removing item with ID:", item.id);

    setProcessingItems((prev) => new Set(prev).add(item.id));

    try {
      await removeFromWishlist(item.id);
    } finally {
      setProcessingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleClearWishlist = async () => {
    await clearWishlist();
  };

  const {
    addToCart,
    setCartFromBackend,
    transformBackendToFrontend,
    setCartItems,
  } = useCart();

  const handleMoveToCart = async (item) => {
    // Prevent multiple clicks
    if (processingItems.has(item.id)) return;

    setProcessingItems((prev) => new Set(prev).add(item.id));

    try {
      await moveToCart(
        item,
        addToCart,
        setCartFromBackend,
        transformBackendToFrontend,
        setCartItems,
      );

      // if (result.success) {
      //   // setIsCartOpen(true);
      //   toast.success(currentLanguage === "EN"? "Moved to cart": "تم النقل الي السلة")
      // }
    } finally {
      setProcessingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const getProductLink = (pro) => {
    if (!authContext?.systemCollections?.length) return;

    const collection = authContext?.systemCollections.find(
      (col) => col.id === (pro?.collection?.id || pro?.collection_id),
    );

    const collectionName = collection?.name_url
      .toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");

    const category = collection?.categories.find(
      (cat) => cat.id === (pro?.category?.id || pro?.category_id),
    );

    const catName = category?.name_url
      .toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");

    const subCatName = category?.sub_categories
      .find((cat) => cat.id === (pro?.subCategory?.id || pro?.sub_category_id))
      ?.name_url.toString()
      .toLowerCase()
      .trim()
      .split(" ")
      .join("-");

    return !subCatName
      ? `/collection/${collectionName}/${catName}/${pro.id}`
      : `/collection/${collectionName}/${catName}/${subCatName}/${pro.id}`;
  };

  const getProductImage = (product) => {
    if (product.images && product.images.length > 0) {
      return product.images[0].image_url;
    }
    return "/assets/images/placeholder.png";
  };

  const getProductName = (product) => {
    return currentLanguage === "EN"
      ? product.name_en || product.nameEn
      : product.name_ar || product.nameAr || product.name_en || product.nameEn;
  };

  const getVariantPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      const variant = product.variants[0];
      return {
        price: variant.price || 0,
        discountPrice: variant.discountPrice || null,
      };
    }
    return { price: 0, discountPrice: null };
  };

  const calculateDiscount = (price, discountPrice) => {
    if (discountPrice && discountPrice < price) {
      return Math.round(((price - discountPrice) / price) * 100);
    }
    return 0;
  };

  const getVariantStock = (product) => {
    if (!product.variants || product.variants.length === 0) return 0;
    const variant =
      product.variants.find((v) => {
        return (
          v.quantity - v.reservedQuantity > 0 ||
          v.withoutBoxQuantity - v.withoutBoxReservedQuantity > 0
        );
      }) || product.variants[0];
    // console.log((variant.quantity - variant.reservedQuantity) + " ----"+ (variant.withoutBoxQuantity - variant.withoutBoxReservedQuantity));
    return (
      variant.quantity - variant.reservedQuantity ||
      variant.withoutBoxQuantity - variant.withoutBoxReservedQuantity
    );
  };

  if (isLoading) {
    return (
      <div className="wishlist-page">
        <div className="wishlist-container">
          <div className="wishlist-loading">
            <Loader size={48} className="spinner" />
            <p>
              {currentLanguage === "EN"
                ? "Loading wishlist..."
                : "جاري تحميل قائمة المفضلة..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (wishlistItems.length === 0) {
    return (
      <div className="wishlist-page">
        <div className="wishlist-container">
          <div className="wishlist-empty">
            <Heart size={80} className="empty-icon" />
            <h2>
              {currentLanguage === "EN"
                ? "Your Wishlist is Empty"
                : "قائمة المفضلة فارغة"}
            </h2>
            <p>
              {currentLanguage === "EN"
                ? "Add items you like to your wishlist. Review them anytime and easily move them to your cart."
                : "أضف المنتجات التي تعجبك إلى قائمة المفضلة. راجعها في أي وقت وانقلها بسهولة إلى سلة التسوق."}
            </p>
            <Link to="/" className="continue-shopping-btn">
              {currentLanguage === "EN" ? "Continue Shopping" : "تابع التسوق"}
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-page">
      <div className="wishlist-container">
        {/* Header */}
        <div className="wishlist-header">
          <div className="header-left">
            <Heart size={32} className="header-icon" />
            <div className="header-text">
              <h1>
                {currentLanguage === "EN" ? "My Wishlist" : "قائمة المفضلة"}
              </h1>
              <p>
                {currentLanguage === "EN"
                  ? `${wishlistCount} ${wishlistCount === 1 ? "item" : "items"}`
                  : `${wishlistCount} ${wishlistCount === 1 ? "منتج" : "منتجات"}`}
              </p>
            </div>
          </div>
          <button className="clear-all-btn" onClick={handleClearWishlist}>
            <Trash2 size={18} />
            {currentLanguage === "EN" ? "Clear All" : "حذف الكل"}
          </button>
        </div>

        {/* Wishlist Items */}
        <div className="wishlist-items">
          {wishlistItems.map((item) => {
            const product = item.product;
            const isProcessing = processingItems.has(item.id);
            const { price, discountPrice } = getVariantPrice(product);
            const discount = calculateDiscount(price, discountPrice);
            const stock = getVariantStock(product);

            return (
              <div
                key={item.id}
                className={`wishlist-item ${isProcessing ? "processing" : ""}`}
              >
                <div className="item-image">
                  <Link to={getProductLink(product)}>
                    <img
                      src={getProductImage(product)}
                      alt={getProductName(product)}
                    />
                  </Link>
                </div>

                <div className="item-details">
                  <Link to={getProductLink(product)} className="item-name">
                    {getProductName(product)}
                  </Link>

                  <div className="item-price">
                    {discountPrice && discountPrice < price ? (
                      <>
                        <span className="price-current">
                          EGP {discountPrice.toFixed(2)}
                        </span>
                        <span className="price-original">
                          EGP {price.toFixed(2)}
                        </span>
                        <span className="price-discount">{discount}% OFF</span>
                      </>
                    ) : (
                      <span className="price-regular">
                        EGP {price.toFixed(2)}
                      </span>
                    )}
                  </div>

                  <div className="item-stock">
                    {stock > 0 ? (
                      <span className="in-stock">
                        {currentLanguage === "EN" ? "In Stock" : "متوفر"}
                      </span>
                    ) : (
                      <span className="out-of-stock">
                        {currentLanguage === "EN"
                          ? "Out of Stock"
                          : "غير متوفر"}
                      </span>
                    )}
                  </div>

                  <div className="item-actions">
                    <button
                      className="add-to-cart-btn"
                      onClick={() => handleMoveToCart(item)}
                      disabled={isProcessing || stock === 0}
                    >
                      {isProcessing ? (
                        <Loader size={18} className="spinner" />
                      ) : (
                        <ShoppingCart size={18} />
                      )}
                      {currentLanguage === "EN" ? "Move to Cart" : "نقل للسلة"}
                    </button>

                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveItem(item)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? (
                        <Loader size={18} className="spinner" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                      {currentLanguage === "EN" ? "Remove" : "حذف"}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Wishlist;
